<template>
  <div>
    <v-data-table
      :loading="loading"
      :headers="aggregationTable.headers"
      :items="aggregationTable.items"
      :header-props="{ sortByText: 'ソート' }"
      :footer-props="{ itemsPerPageText: '行/ページ:' }"
      no-data-text="データがありません"
      loading-text="読込中..."
      hide-default-footer
      :items-per-page="-1"
      class="aggregation-table fixed-first-column"
      :class="{ 'fixed-first-column': isUserAggregation }"
    >
      <template #top>
        <v-toolbar
          flat
          color="transparent"
        >
          <v-toolbar-title>{{ aggregationTable.name }}</v-toolbar-title>
          <v-spacer />

          <v-btn
            color="primary"
            :ripple="false"
            :disabled="loading || aggregationTable.items.length === 0 || isAllSaved"
            small
            class="mr-2"
            @click.prevent="$emit('save-all', aggregationTable.id)"
          >
            保存
          </v-btn>

          <v-btn
            :ripple="false"
            :loading="loading"
            :disabled="loading"
            color="secondary"
            small
            @click.prevent="$emit('reload', aggregationTable.id)"
          >
            <v-icon left>
              {{ icons.mdiReload }}
            </v-icon>
            再集計
          </v-btn>

          <v-divider
            vertical
            inset
            class="mx-2"
          />

          <v-menu
            bottom
            left
          >
            <template #activator="{ on: menuOn, attrs: menuAttrs }">
              <v-btn
                icon
                v-bind="menuAttrs"
                v-on="menuOn"
              >
                <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
              </v-btn>
            </template>

            <template #default="menu">
              <v-list
                :disabled="loading"
                dense
              >
                <v-subheader>操作</v-subheader>

                <AggregationUsersDialogListItem
                  v-if="isUserAggregation"
                  :disabled="loading"
                  :aggregation-users="aggregationUsers"
                  :available-users="availableUsers"
                  @create="$emit('create-user-aggregation', $event)"
                  @fetch-available-users="$emit('fetch-available-users', $event)"
                  @destroy="$emit('destroy-user-aggregation', $event)"
                  @cancel="menu.value = false"
                />

                <DataTableCopyListItemBtn
                  :disabled="loading"
                  :headers="aggregationTable.headers"
                  :items="aggregationTable.items"
                  :display-sum-row="displaySumRow"
                  :sum-row="accRow"
                />

                <v-list-item
                  :disabled="loading"
                  @click="displaySumRow = !displaySumRow"
                >
                  <v-list-item-title>
                    {{ sumRowMessage }}
                  </v-list-item-title>
                </v-list-item>

                <v-dialog max-width="400">
                  <template #activator="{ on: listOn, attrs: listAttrs }">
                    <v-list-item
                      :disabled="loading"
                      v-bind="listAttrs"
                      v-on="listOn"
                    >
                      <v-list-item-title class="error--text">
                        リセットして再集計
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                  <template #default="dialog">
                    <v-card>
                      <v-toolbar
                        flat
                        color="transparent"
                      >
                        確認
                      </v-toolbar>
                      <v-card-text>
                        保存された結果をすべてクリアし、再集計を行います。よろしいですか？
                      </v-card-text>
                      <v-card-actions class="justify-end">
                        <v-btn
                          text
                          @click="closeDialogs(dialog, menu)"
                        >
                          キャンセル
                        </v-btn>
                        <v-btn
                          color="primary"
                          @click="handleRefresh(dialog, menu)"
                        >
                          OK
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </template>
                </v-dialog>
              </v-list>
            </template>
          </v-menu>
        </v-toolbar>

        <v-alert
          v-if="isFailed || errorMessage"
          type="error"
          dense
          dismissible
          class="ma-2"
        >
          【エラー】{{ errorMessage }} <br />
          再集計してもエラーが継続する場合はサポートまでお問い合わせください
        </v-alert>
      </template>

      <template
        v-for="header in aggregationTable.businessRuleHeaders"
        #[`item.${header.value}`]="{ item }"
      >
        <AggregationTableCell
          :key="`cell-${item.id || item.name}-${header.value}`"
          :value="item[header.value]"
          :is-boolean="isBooleanItem(item, header.value)"
          :is-number="isNumberItem(item, header.value)"
          :is-new="isNew(item, header.value)"
          :is-editing="isEditingCell(item, header)"
          :disabled="loading"
          :input-type="inputType(item, header.value)"
          :number-text="numberText(item, header.value)"
          @click="openEditMenu($event, item, header)"
        />
      </template>

      <template #[`item.name`]="{ item }">
        <v-btn
          v-if="isUserAggregation"
          class="user-name-button"
          small
          text
          :ripple="false"
          @click="$emit('select-user', buildSelectUserPayload(item))"
        >
          {{ item.name }}
        </v-btn>
        <span v-else>{{ item.name }}</span>
      </template>

      <template #[`body.prepend`]>
        <tr v-if="displaySumRow">
          <td
            v-for="header in aggregationTable.headers"
            :key="header.value"
            :class="['font-weight-bold', header.cellClass || header.class]"
          >
            {{ accRow[header.value] }}
          </td>
        </tr>
      </template>
    </v-data-table>

    <AggregationTableEditMenu
      :visible="editMenuOpen"
      :title="editing.header ? editing.header.text : ''"
      :value="editing.value"
      :is-boolean="editing.isBoolean"
      :is-number="editing.isNumber"
      :input-type="editing.type"
      :disabled="editing.disabled"
      :menu-style="editMenuStyle"
      @cancel="editMenuOpen = false"
      @save="saveEditing"
    />
  </div>
</template>

<script>
import {
  computed, ref, reactive, watch, onMounted, onUnmounted, nextTick,
} from '@vue/composition-api'
import { mdiReload, mdiDotsVertical } from '@mdi/js'
import useAggregationTable from '../composables/useAggregationTable'
import AggregationUsersDialogListItem from './AggregationUsersDialogListItem.vue'
import AggregationTableCell from './AggregationTableCell.vue'
import AggregationTableEditMenu from './AggregationTableEditMenu.vue'
import DataTableCopyListItemBtn from '@/views/v2-temp/common/components/DataTableCopyListItemBtn.vue'

const MENU_WIDTH = 300
const MENU_HEIGHT = 200
const MENU_MARGIN = 10

export default {
  name: 'AggregationTable',
  components: {
    AggregationUsersDialogListItem,
    AggregationTableCell,
    AggregationTableEditMenu,
    DataTableCopyListItemBtn,
  },
  props: {
    aggregation: {
      type: Object,
      default: () => ({
        attributes: {
          id: null,
          name: '',
          description: '',
          businessRules: { data: [] },
          results: { data: [] },
          contexts: { data: [{ attributes: { results: { data: [] } } }] },
        },
      }),
    },
    aggregationUsers: {
      type: Array,
      default: () => [],
    },
    availableUsers: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    status: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  setup(props, { emit }) {
    // State
    const displaySumRow = ref(false)
    const editMenuOpen = ref(false)
    const editMenuPosition = ref({ top: 0, left: 0 })

    const editing = reactive({
      item: null,
      header: null,
      value: null,
      isBoolean: false,
      isNumber: false,
      type: 'text',
      disabled: false,
    })

    // Composable
    const {
      aggregationTable,
      isUserAggregation,
      isBooleanItem,
      isNumberItem,
      isNew,
      isNotEditable,
      inputType,
      numberText,
      saveResultValue,
      onMenuInput,
    } = useAggregationTable(props, emit)

    // Computed
    const isAllSaved = computed(() => (
      aggregationTable.value.items.every(item => (
        aggregationTable.value.headers.every(header => {
          const businessRuleId = Number(header.value)
          if (typeof businessRuleId === 'number' && !Number.isNaN(businessRuleId)) {
            return !isNew.value(item, businessRuleId)
          }

          return true
        })
      ))
    ))

    const isFailed = computed(() => (
      ['failed', 'lambda_failed'].includes(props.status)
    ))

    const sumRowMessage = computed(() => (
      displaySumRow.value ? '合計行を非表示' : '合計行を表示'
    ))

    const accRow = computed(() => {
      const sums = {}
      aggregationTable.value.headers.forEach(header => {
        if (header.value === 'name') {
          sums[header.value] = '合計'
        } else {
          const total = aggregationTable.value.items.reduce((acc, item) => {
            const val = item[header.value]
            if (val === null || val === undefined || val === '') return acc
            const numVal = Number(val)

            return Number.isNaN(numVal) ? acc : acc + numVal
          }, 0)
          sums[header.value] = total
        }
      })

      return sums
    })

    const editMenuStyle = computed(() => {
      if (!editMenuOpen.value || !editMenuPosition.value) {
        return {}
      }

      return {
        position: 'fixed',
        top: `${editMenuPosition.value.top}px`,
        left: `${editMenuPosition.value.left}px`,
        minWidth: `${MENU_WIDTH}px`,
        zIndex: 1001,
      }
    })

    // Methods
    const isEditingCell = (item, header) => (
      editMenuOpen.value && editing.item === item && editing.header === header
    )

    const calculateMenuPosition = rect => {
      let left = rect.right
      let { top } = rect

      if (left + MENU_WIDTH > window.innerWidth) {
        left = rect.left - MENU_WIDTH
      }

      if (top + MENU_HEIGHT > window.innerHeight) {
        top = window.innerHeight - MENU_HEIGHT - MENU_MARGIN
      }

      if (left < 0) left = MENU_MARGIN
      if (top < 0) top = MENU_MARGIN

      return { top, left }
    }

    const openEditMenu = async (event, item, header) => {
      if (props.loading) return
      if (isNotEditable.value(item, header.value)) return

      if (editMenuOpen.value) {
        editMenuOpen.value = false
        await nextTick()
      }

      const rect = event.currentTarget.getBoundingClientRect()
      editMenuPosition.value = calculateMenuPosition(rect)

      editing.item = item
      editing.header = header
      editing.isBoolean = isBooleanItem.value(item, header.value)
      editing.isNumber = isNumberItem.value(item, header.value)
      editing.type = inputType.value(item, header.value)
      editing.disabled = isNotEditable.value(item, header.value)

      const raw = item[header.value]
      editing.value = editing.isBoolean ? !!raw : raw

      editMenuOpen.value = true
    }

    const saveEditing = newValue => {
      if (!editing.item || !editing.header) return

      const { item } = editing
      const key = editing.header.value

      item[key] = newValue

      const menuProxy = {
        get value() { return editMenuOpen.value },
        set value(v) { editMenuOpen.value = v },
      }

      saveResultValue(item, key, item[key], menuProxy)
    }

    const buildSelectUserPayload = item => ({
      userId: item.id,
      userName: item.name,
    })

    const closeDialogs = (dialog, menu) => {
      // eslint-disable-next-line no-param-reassign
      dialog.value = false
      // eslint-disable-next-line no-param-reassign
      menu.value = false
    }

    const handleRefresh = (dialog, menu) => {
      emit('refresh', aggregationTable.value.id)
      closeDialogs(dialog, menu)
    }

    const handleKeydown = event => {
      if (event.key === 'Escape' && editMenuOpen.value) {
        editMenuOpen.value = false
      }
    }

    // Watchers
    watch(editMenuOpen, val => {
      if (editing.item && editing.header) {
        onMenuInput(val, editing.item, editing.header.value)
      }
    })

    // Lifecycle
    onMounted(() => {
      window.addEventListener('keydown', handleKeydown)
    })

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown)
    })

    return {
      // State
      displaySumRow,
      editMenuOpen,
      editing,

      // Computed
      aggregationTable,
      isUserAggregation,
      isAllSaved,
      isFailed,
      sumRowMessage,
      accRow,
      editMenuStyle,

      // Methods
      isBooleanItem,
      isNumberItem,
      isNew,
      inputType,
      numberText,
      isEditingCell,
      openEditMenu,
      saveEditing,
      buildSelectUserPayload,
      closeDialogs,
      handleRefresh,

      // Icons
      icons: {
        mdiReload,
        mdiDotsVertical,
      },
    }
  },
}
</script>

<style lang="scss">
@import '~@core/preset/preset/mixins.scss';

@include theme--child(aggregation-table) using ($material) {
  th.fixed-cell,
  td.fixed-cell {
    position: sticky !important;
    position: -webkit-sticky !important;
    left: 0;
    z-index: 3;
    background: map-deep-get($material, 'app-bar');
  }

  th.fixed-cell {
    z-index: 4;
  }

  .v-progress-linear {
    z-index: 5;
  }
}

.aggregation-table {
  white-space: nowrap;
}

// ユーザー名セルのボタン（データセット管理画面 卓タブ内のテーブルセル v-btn を踏襲）。
// 通常時は枠線なし、ホバー時に薄グレー背景のみ表示。
// フォント色は親要素（テーブルセル）の色を inherit することで、Vuetify の
// ダーク/ライトテーマに自動追従させる。
// Vuetify の v-btn は text モードで .v-btn__content の opacity を下げる挙動があるため、
// 明示的に opacity: 1 で上書きして通常時もはっきり表示する。
.user-name-button.v-btn {
  cursor: pointer;
  color: inherit !important;
  opacity: 1 !important;
  border-radius: 4px;
  text-transform: none;
  font-weight: 400;
  padding: 0 8px;
  min-width: 0;

  .v-btn__content {
    opacity: 1 !important;
  }

  &:hover {
    background-color: rgba(150, 150, 150, 0.15);
  }

  &::before {
    background-color: transparent !important;
  }
}
</style>
